import React, { useEffect, useRef, useState } from "react";
import { Check, PenLine, RefreshCw, Trash2 } from "lucide-react";
import {
  AppButton, Callout, Checkbox, ConfirmModal, FileUpload, SegmentedControl, SignaturePad,
} from "../../ui";
import apiClient from "../../../utils/apiClient";
import { loadImageFile, prepareSignature } from "./signatureImage";

const MODES = [
  { value: "draw", label: "Draw it" },
  { value: "upload", label: "Upload a photo or scan" },
];

/** A signature on paper-white, whatever the app theme — it is what prints. */
function SignatureTile({ src, alt }) {
  return (
    <div className="tw-flex tw-h-24 tw-w-full tw-max-w-[20rem] tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-[var(--paper)] tw-p-2">
      <img src={src} alt={alt} className="tw-max-h-full tw-max-w-full tw-object-contain" />
    </div>
  );
}

/**
 * Settings → Invoices → Authorised signatory → the signature itself.
 *
 * Saved the moment it is added (its own upload, like a logo), under a private
 * storage key that only ever reaches a browser as a ten-minute link. Drawn
 * signatures are trimmed; uploads are also made transparent where they are
 * paper-white, so a phone photo of a signature prints like ink.
 */
export default function SignatureEditor({ signatureUrl, hasImage, onSaved, showToast }) {
  const [editing, setEditing] = useState(!hasImage);
  const [mode, setMode] = useState("draw");
  const [busy, setBusy] = useState(null); // 'process' | 'upload' | 'remove'
  const [error, setError] = useState("");
  const [hasInk, setHasInk] = useState(false);
  const [files, setFiles] = useState([]);
  const [source, setSource] = useState(null); // the uploaded image, before processing
  const [removeBackground, setRemoveBackground] = useState(true);
  const [prepared, setPrepared] = useState(null); // { blob, url }
  const [confirmRemove, setConfirmRemove] = useState(false);
  const padRef = useRef(null);

  useEffect(() => { if (!hasImage) setEditing(true); }, [hasImage]);

  // An uploaded image is re-processed whenever the background choice changes.
  useEffect(() => {
    if (!source) return undefined;
    let live = true;
    setBusy("process");
    setError("");
    prepareSignature(source, { removeBackground })
      .then(({ blob }) => { if (live) setPrepared({ blob, url: URL.createObjectURL(blob) }); })
      .catch((e) => { if (live) { setPrepared(null); setError(e.message); } })
      .finally(() => { if (live) setBusy(null); });
    return () => { live = false; };
  }, [source, removeBackground]);

  useEffect(() => () => { if (prepared?.url) URL.revokeObjectURL(prepared.url); }, [prepared]);

  const reset = () => {
    setFiles([]);
    setSource(null);
    setPrepared(null);
    setError("");
    padRef.current?.clear();
  };

  const pick = async (picked) => {
    setFiles(picked);
    setPrepared(null);
    setError("");
    if (!picked[0]) { setSource(null); return; }
    try {
      setSource(await loadImageFile(picked[0]));
    } catch (e) {
      setError(e.message);
    }
  };

  const upload = async (blob) => {
    setBusy("upload");
    setError("");
    try {
      const body = new FormData();
      body.append("file", blob, "signature.png");
      const { data } = await apiClient.post("/invoice-settings/signature", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSaved?.(data);
      reset();
      setEditing(false);
      showToast?.("Signature saved.");
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't save the signature. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const useDrawn = async () => {
    const canvas = padRef.current?.canvas;
    if (!canvas || padRef.current.isEmpty()) return;
    setBusy("process");
    try {
      const { blob } = await prepareSignature(canvas, { removeBackground: false });
      await upload(blob);
    } catch (e) {
      setError(e.message);
      setBusy(null);
    }
  };

  const remove = async () => {
    setConfirmRemove(false);
    setBusy("remove");
    try {
      const { data } = await apiClient.delete("/invoice-settings/signature");
      onSaved?.(data);
      setEditing(true);
      showToast?.("Signature removed. Printed invoices now have room to sign by hand.");
    } catch (e) {
      showToast?.(e?.response?.data?.message || "Couldn't remove the signature.", "error");
    } finally {
      setBusy(null);
    }
  };

  if (hasImage && !editing) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-2">
        <SignatureTile src={signatureUrl} alt="Your saved signature" />
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <AppButton variant="outline" size="sm" onClick={() => setEditing(true)} disabled={Boolean(busy)}>
            <RefreshCw size={14} /> Replace
          </AppButton>
          <AppButton variant="ghost" size="sm" onClick={() => setConfirmRemove(true)} disabled={Boolean(busy)}>
            <Trash2 size={14} /> {busy === "remove" ? "Removing…" : "Remove"}
          </AppButton>
        </div>
        <ConfirmModal
          show={confirmRemove}
          onHide={() => setConfirmRemove(false)}
          onConfirm={remove}
          title="Remove the signature?"
          message="Invoices keep the signatory's name and designation, with an empty space above them to sign by hand."
          confirmText="Remove"
          variant="danger"
        />
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <SegmentedControl
        label="How to add the signature"
        value={mode}
        onChange={(next) => { setMode(next); setError(""); }}
        options={MODES}
      />

      {mode === "draw" ? (
        <>
          <SignaturePad ref={padRef} onChange={setHasInk} />
          <span className="tw-text-xs tw-text-content-muted">
            Sign with your mouse, or open this page on a phone or tablet and sign with your finger.
          </span>
        </>
      ) : (
        <>
          <FileUpload
            files={files}
            onChange={pick}
            accept=".png,.jpg,.jpeg,.webp"
            multiple={false}
            maxFiles={1}
            maxSizeMB={10}
            disabled={busy === "upload"}
            hint="A photo or scan of the signature on plain white paper — PNG, JPG or WebP"
          />
          {source && (
            <Checkbox
              checked={removeBackground}
              onChange={(e) => setRemoveBackground(e.target.checked)}
              label="Remove the white paper behind it"
              description="Keeps only the ink, so it prints cleanly over the invoice."
            />
          )}
          {busy === "process" && <span className="tw-text-sm tw-text-content-muted">Preparing the signature…</span>}
          {prepared && <SignatureTile src={prepared.url} alt="The signature as it will print" />}
        </>
      )}

      {error && <Callout tone="danger" role="alert">{error}</Callout>}

      <div className="tw-flex tw-flex-wrap tw-gap-2">
        {mode === "draw" ? (
          <AppButton size="sm" onClick={useDrawn} disabled={!hasInk || Boolean(busy)}>
            <PenLine size={14} /> {busy === "upload" ? "Saving…" : "Use this signature"}
          </AppButton>
        ) : (
          <AppButton size="sm" onClick={() => prepared && upload(prepared.blob)} disabled={!prepared || Boolean(busy)}>
            <Check size={14} /> {busy === "upload" ? "Saving…" : "Use this signature"}
          </AppButton>
        )}
        {hasImage && (
          <AppButton variant="ghost" size="sm" onClick={() => { reset(); setEditing(false); }} disabled={busy === "upload"}>
            Keep the current one
          </AppButton>
        )}
      </div>
    </div>
  );
}
